import { useCallback, useRef, useState } from 'react';
/**
 * Generic controlled/uncontrolled state channel for Aether internals.
 *
 * The setter takes a concrete next value — never a React functional updater —
 * so function-typed values are stored as-is, and its identity is stable across
 * renders. Instantiate once per independent channel. Switching between
 * controlled and uncontrolled after mount is unsupported and warns in
 * development; the channel keeps whichever value its current mode owns.
 */
export function useControllableValue({ value, defaultValue, onValueChange, }) {
    // Lazy initializer so a function-typed default is stored, not invoked.
    const [uncontrolledValue, setUncontrolledValue] = useState(() => defaultValue);
    const isControlled = value !== undefined;
    const isControlledRef = useRef(isControlled);
    if (process.env.NODE_ENV !== 'production' && isControlledRef.current !== isControlled) {
        console.warn(`useControllableValue: a channel switched from ${isControlledRef.current ? 'controlled' : 'uncontrolled'} to ${isControlled ? 'controlled' : 'uncontrolled'}. Mode is chosen at first render and ` +
            'switching it is unsupported; keep `value` defined (controlled) or `undefined` ' +
            '(uncontrolled) for the lifetime of the component.');
    }
    isControlledRef.current = isControlled;
    const onValueChangeRef = useRef(onValueChange);
    onValueChangeRef.current = onValueChange;
    const setValue = useCallback((nextValue) => {
        if (!isControlledRef.current) {
            // Store through an updater thunk so a function-typed value is kept
            // as a value instead of being run as a React functional updater.
            setUncontrolledValue(() => nextValue);
        }
        onValueChangeRef.current?.(nextValue);
    }, []);
    return [value !== undefined ? value : uncontrolledValue, setValue];
}
//# sourceMappingURL=useControllableValue.js.map